import { Alert, Button, Card, Description, Link, Surface, Tabs } from "@heroui/react";
import type { Key } from "@heroui/react";
import type { PublicProduct } from "../../../shared/types";
import { PublicMenuNavbar } from "../../components/PublicMenuNavbar";
import { PublicMenuThemeProvider } from "../../components/PublicMenuThemeProvider";
import { useTenant } from "../../contexts/TenantContext";
import { formatLocal, formatUsd } from "../../lib/currency";
import { productCanBeConfigured, productDisplayPrice } from "./configuration";
import type { PublicMenuController } from "./usePublicMenuController";

import { PublicMenuLoadingScreen } from "./shared/PublicMenuLoadingScreen";
import { BranchPicker } from "./shared/MenuControls";
import { PublicMenuOverlays } from "./shared/PublicMenuOverlays";

// Compatibility for existing callers; the implementation is shared by all layouts.
export { isIngredientSelected, withIngredientSelection } from "./shared/ProductCustomizer";

function ProductCard({
  product,
  branchId,
  tenant,
  products,
  onOpen,
}: {
  product: PublicProduct;
  branchId: string;
  tenant: NonNullable<ReturnType<typeof useTenant>["tenant"]>;
  products: ReadonlyMap<string, PublicProduct>;
  onOpen(): void;
}) {
  const unavailable = !productCanBeConfigured(product, products, branchId);
  const price = productDisplayPrice(product, branchId);
  return (
    <Card
      role="article"
      className="menu-product-card menu-panel group flex h-full overflow-hidden border p-0 transition hover:-translate-y-1 hover:shadow-surface"
      variant="default"
    >
      <Card.Content className="relative overflow-hidden p-0">
        <div className="relative flex aspect-[16/11] items-center justify-center overflow-hidden bg-[color:var(--menu-surface-alt)]">
          {product.images[0] ? (
            <img
              alt={product.name}
              className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-[1.04]"
              loading="lazy"
              src={product.images[0]}
            />
          ) : (
            <Surface
              className="grid h-full w-full place-items-center text-5xl"
              variant="tertiary"
              aria-label="Producto sin imagen"
            >
              ○
            </Surface>
          )}
        </div>
        {product.promoType !== "none" && (
          <span className="menu-promo absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-black">
            PROMO
          </span>
        )}
        {unavailable && (
          <span className="menu-panel absolute inset-x-3 bottom-3 rounded-[var(--menu-radius)] px-3 py-2 text-center text-xs font-bold text-danger shadow-surface">
            No disponible en esta sucursal
          </span>
        )}
      </Card.Content>
      <Card.Header className="px-3.5 pb-1.5 pt-3">
        <Card.Title className="menu-text-primary line-clamp-2 text-[15px] font-black leading-[1.08] tracking-[-0.015em]">
          {product.name}
        </Card.Title>
        <Card.Description className="menu-text-secondary mt-1 line-clamp-2 text-xs leading-4">
          {product.description || "Preparado al momento."}
        </Card.Description>
      </Card.Header>
      <Card.Footer className="px-3.5 pb-3 pt-0">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <strong className="menu-price text-base font-black leading-none sm:text-lg">
                {price.from && "Desde "}
                {formatUsd(price.cents)}
              </strong>
              {product.promoType !== "none" &&
                product.priceMode !== "variante" && (
                  <span className="menu-price text-[11px] opacity-60 line-through">
                    {formatUsd(product.priceUsdCents)}
                  </span>
                )}
            </div>
            <p className="menu-text-muted mt-1 truncate text-[11px] font-semibold">
              {formatLocal(
                price.cents,
                tenant.exchangeRate,
                tenant.localCurrency,
                tenant.currencyDecimals,
                tenant.currencySymbol,
              )}
            </p>
          </div>
          <Button
            className="min-w-20 shrink-0 px-3 py-2 text-xs font-black"
            aria-label={`Configurar ${product.name}`}
            isDisabled={unavailable}
            onPress={onOpen}
          >
            {product.type === "combo" ? "Armar" : "Elegir"}
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
}

export function DefaultPublicMenuLayout({
  controller,
  className,
}: {
  controller: PublicMenuController;
  className?: string;
}) {
  const {
    activeCategory,
    aiStatus,
    applyAiCartAction,
    branchId,
    brandLoaderVisible,
    cartOpen,
    closeCustomizer,
    editLine,
    editingLine,
    error,
    itemCount,
    lines,
    loading,
    openProduct,
    products,
    quickAddProduct,
    reload,
    selectBranch,
    selectedBranch,
    selectedProduct,
    setActiveCategory,
    setCartOpen,
    tenant,
    totalUsdCents,
  } = controller;

  if (loading || brandLoaderVisible) {
    return <PublicMenuLoadingScreen />;
  }

  const brandLoaderOverlay = brandLoaderVisible ? (
    <div className="fixed inset-0 z-50">
      <PublicMenuLoadingScreen />
    </div>
  ) : null;

  if (error || !tenant) {
    return (
      <main className="grid min-h-screen place-items-center bg-background p-5">
        {brandLoaderOverlay}
        <Card
          className="w-full max-w-lg border border-border text-center"
          variant="default"
        >
          <Card.Header>
            <Card.Title className="text-2xl font-black">
              Menú no disponible
            </Card.Title>
            <Card.Description>
              {error || "No se encontró el negocio."}
            </Card.Description>
          </Card.Header>
          <Card.Content>
            <Alert status="danger">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>No pudimos cargar esta carta</Alert.Title>
                <Alert.Description>
                  Revisa tu conexión e intenta nuevamente.
                </Alert.Description>
              </Alert.Content>
            </Alert>
          </Card.Content>
          <Card.Footer className="justify-center">
            <Button onPress={() => void reload()}>Reintentar</Button>
          </Card.Footer>
        </Card>
      </main>
    );
  }

  const heroImage =
    tenant.categories.find((category) => category.imageUrl)?.imageUrl ??
    tenant.categories
      .flatMap((category) => category.products)
      .find((product) => product.images[0])?.images[0];
  const totalMenuProducts = tenant.categories.reduce(
    (total, category) => total + category.products.length,
    0,
  );
  const activeBranchCount = tenant.branches.length;

  return (
    <PublicMenuThemeProvider
      theme={tenant.theme}
      className={className}
      fontLoadingFallback={<PublicMenuLoadingScreen />}
      updateDocumentTheme
      waitForFonts
    >
      {brandLoaderOverlay}
        <div className="menu-text-primary min-h-screen bg-[var(--menu-background)]">
        <PublicMenuNavbar
          name={tenant.name}
          subtitle={selectedBranch?.name || "Selecciona una sucursal"}
          logoUrl={tenant.logoUrl ?? undefined}
          navbarStyle={tenant.theme.appearance.navbarStyle}
          trailing={
            <nav
              aria-label="Acciones del menú"
              className="flex items-center gap-2"
            >
              <Link
                className="hidden rounded-[var(--menu-radius)] px-3 py-2 text-sm font-semibold text-current hover:bg-[var(--menu-surface-alt)] hover:text-[var(--menu-text-primary)] sm:block"
                href="/employee"
              >
                Empleados
              </Link>
              <Button
                className="relative"
                variant="secondary"
                onPress={() => setCartOpen(true)}
              >
                Pedido · {formatUsd(totalUsdCents)}
                {itemCount > 0 && (
                  <span className="absolute -right-2 -top-2 grid min-h-6 min-w-6 place-items-center rounded-full bg-success px-1 text-xs font-black text-success-foreground">
                    {itemCount}
                  </span>
                )}
              </Button>
            </nav>
          }
        />

        <main className="pb-32">
          <section
            className="relative isolate overflow-hidden px-4 py-6 sm:px-6 sm:py-10 lg:py-14"
            aria-labelledby="public-menu-hero-title"
          >
            <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_12%,var(--menu-accent)_0,transparent_28%),radial-gradient(circle_at_72%_78%,var(--menu-primary)_0,transparent_30%)] opacity-20" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-px bg-gradient-to-r from-transparent via-[var(--menu-border)] to-transparent" />
            <div className="mx-auto grid max-w-7xl items-center gap-8 lg:grid-cols-[1.02fr_.98fr]">
              <div className="relative z-10 flex flex-col justify-between gap-8 p-5 sm:p-8 lg:p-10">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex rounded-full bg-[var(--menu-accent)] px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-[var(--menu-accent-foreground)] shadow-sm">
                      Carta del día
                    </span>
                  </div>
                  <h2
                    id="public-menu-hero-title"
                    className="menu-text-primary mt-5 max-w-2xl text-4xl font-black leading-[0.92] tracking-[-0.055em] sm:text-6xl lg:text-7xl"
                  >
                    Tu carta digital, lista para pedir sin perder tiempo.
                  </h2>
                  <p className="menu-text-secondary mt-5 max-w-xl text-sm leading-6 sm:text-base">
                    {tenant.description ||
                      "Explora el menú, elige tu sucursal, personaliza cada producto y arma tu pedido desde una experiencia rápida y clara."}
                  </p>
                  <div className="mt-6 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-[var(--menu-radius)] border border-border bg-[var(--menu-surface-alt)]/70 p-4">
                      <p className="text-2xl font-black text-[var(--menu-accent)]">
                        {totalMenuProducts}
                      </p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-[var(--menu-text-muted)]">
                        Productos
                      </p>
                    </div>
                    <div className="rounded-[var(--menu-radius)] border border-border bg-[var(--menu-surface-alt)]/70 p-4">
                      <p className="text-2xl font-black text-[var(--menu-accent)]">
                        {tenant.categories.length}
                      </p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-[var(--menu-text-muted)]">
                        Categorías
                      </p>
                    </div>
                    <div className="rounded-[var(--menu-radius)] border border-border bg-[var(--menu-surface-alt)]/70 p-4">
                      <p className="text-2xl font-black text-[var(--menu-accent)]">
                        {activeBranchCount}
                      </p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-[var(--menu-text-muted)]">
                        Sucursales
                      </p>
                    </div>
                  </div>
                </div>

                <div className="max-w-2xl rounded-[var(--menu-radius)] border border-border bg-[var(--menu-background)]/80 p-4 shadow-surface backdrop-blur">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                        Elige dónde retirar o recibir
                      </p>
                      <p className="menu-text-secondary mt-1 text-sm">
                        {selectedBranch?.address ||
                          "Selecciona la sucursal más conveniente para continuar."}
                      </p>
                    </div>
                    <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-black text-success">
                      En línea
                    </span>
                  </div>
                  <BranchPicker
                    branches={tenant.branches}
                    selectedId={branchId}
                    onSelect={selectBranch}
                  />
                </div>
              </div>

              {/* <div className="relative min-h-[420px] overflow-hidden rounded-[calc(var(--menu-radius)*1.7)] bg-[var(--menu-surface-alt)] shadow-surface lg:min-h-[520px]">
                {heroImage ? (
                  <img
                    alt="Selección destacada del menú"
                    className="absolute inset-0 h-full w-full scale-105 object-cover transition duration-700 hover:scale-110"
                    src={heroImage}
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-8xl text-[var(--menu-accent)]">
                    ○
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/55" />
                <div className="absolute right-5 top-5 flex items-center gap-2 rounded-full bg-black/45 px-3 py-2 text-xs font-black uppercase tracking-[0.16em] text-white shadow-lg backdrop-blur">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                  Menú activo
                </div>
                <div className="absolute bottom-5 left-5 right-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div className="rounded-[var(--menu-radius)] border border-white/20 bg-black/45 p-4 text-white shadow-lg backdrop-blur-md">
                    <p className="text-xs font-bold uppercase tracking-widest text-white/70">
                      {tenant.name}
                    </p>
                    <p className="mt-1 text-2xl font-black leading-none">
                      Pide fácil, revisa tu orden y confirma desde el carrito.
                    </p>
                  </div>
                  <Button
                    className="menu-critical-cta shadow-lg"
                    onPress={() => setCartOpen(true)}
                  >
                    Ver pedido
                  </Button>
                </div>
              </div> */}
            </div>
          </section>

          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            {tenant.categories.length > 0 ? (
              <Tabs
                className="mt-8 w-full"
                selectedKey={activeCategory}
                variant="primary"
                onSelectionChange={(key: Key) => setActiveCategory(String(key))}
              >
                <Tabs.ListContainer className="scrollbar-none sticky top-[69px] z-20 -mx-4 overflow-x-auto border-y border-border bg-[var(--menu-background)] px-4 sm:-mx-6 sm:px-6">
                  <Tabs.List
                    aria-label="Categorías del menú"
                    className="mx-auto grid w-max min-w-full grid-flow-col grid-rows-2 auto-cols-max justify-start gap-1.5 bg-transparent py-2 pr-10 sm:flex sm:flex-row sm:flex-nowrap sm:gap-2 sm:pr-0"
                  >
                    {tenant.categories.map((category) => (
                      <Tabs.Tab
                        key={category.id}
                        id={category.id}
                        className="h-8 w-auto min-w-max shrink-0 rounded-full bg-[var(--menu-surface-alt)] px-3 py-0 text-xs font-bold text-[var(--menu-text-secondary)] data-[selected=true]:text-[var(--menu-primary-foreground)] sm:h-9 sm:text-sm"
                      >
                        {category.name}
                        <Tabs.Indicator className="rounded-full bg-[var(--menu-primary)] shadow-none" />
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                </Tabs.ListContainer>
                {tenant.categories.map((category) => (
                  <Tabs.Panel
                    key={category.id}
                    id={category.id}
                    className="pt-8"
                  >
                    <section aria-labelledby={`category-${category.id}`}>
                      <div className="mb-5 grid gap-2 border-l-4 border-[var(--menu-accent)] pl-4">
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[var(--menu-accent)]">
                          Para elegir sin prisa
                        </p>
                        <h2
                          id={`category-${category.id}`}
                          className="menu-text-primary text-3xl font-black tracking-tight"
                        >
                          {category.name}
                        </h2>
                        {category.description && (
                          <p className="menu-text-secondary max-w-2xl text-sm leading-6">
                            {category.description}
                          </p>
                        )}
                      </div>
                      {category.products.length > 0 ? (
                        <div className="grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                          {category.products.map((product) => (
                            <ProductCard
                              key={product.id}
                              branchId={branchId}
                              product={product}
                              products={products}
                              tenant={tenant}
                              onOpen={() => openProduct(product)}
                            />
                          ))}
                        </div>
                      ) : (
                        <Surface
                          className="menu-panel-alt grid min-h-48 place-items-center rounded-[var(--menu-radius)] border border-dashed border-border p-8 text-center"
                          variant="secondary"
                        >
                          <div>
                            <h3 className="menu-text-primary font-black">
                              Próximamente
                            </h3>
                            <p className="menu-text-muted mt-1 text-sm">
                              Esta sección todavía no tiene productos
                              disponibles.
                            </p>
                          </div>
                        </Surface>
                      )}
                    </section>
                  </Tabs.Panel>
                ))}
              </Tabs>
            ) : (
              <Surface
                className="menu-panel-alt mt-8 grid min-h-56 place-items-center rounded-[var(--menu-radius)] border border-dashed border-border p-8 text-center"
                variant="secondary"
              >
                <div>
                  <h2 className="menu-text-primary text-xl font-black">
                    La carta se está preparando
                  </h2>
                  <p className="menu-text-muted mt-2 text-sm">
                    Vuelve pronto para descubrir los productos disponibles.
                  </p>
                </div>
              </Surface>
            )}
          </div>
        </main>

        {itemCount > 0 && (
          <div className="fixed inset-x-4 bottom-4 z-30 md:hidden">
            <Button
              fullWidth
              className="menu-critical-cta h-14 justify-between shadow-surface"
              size="lg"
              onPress={() => setCartOpen(true)}
            >
              <span>Ver pedido ({itemCount})</span>
              <strong>{formatUsd(totalUsdCents)}</strong>
            </Button>
          </div>
        )}

        <PublicMenuOverlays controller={controller} />
      </div>
    </PublicMenuThemeProvider>
  );
}
